/**
 * Ready strategies a new user can start a backtest from. Every script here was
 * run through our own engine (the `cryptotrader` CLI, `--engine auto`, Binance
 * candles) on the market it is built for; `results` are those runs, one per
 * year, so the library shows what each did rather than what it promises.
 * The scripts sit next to this file and load only when picked.
 */

export type LibraryCategory = "Trend" | "Breakout" | "Accumulation" | "Learning";
export type LibraryLevel = "Simple" | "Medium" | "Advanced";

export type YearResult = {
  /** Calendar year, or the year to date. */
  year: string;
  /** Net profit in the script's currency, on `capital`. */
  pnl: number;
  trades: number;
  won: number;
  /** Largest fall from a high, in the same currency. */
  maxDrawdown: number;
};

export type LibraryStrategy = {
  id: string;
  name: string;
  category: LibraryCategory;
  level: LibraryLevel;
  /** What it does, in plain words. */
  summary: string;
  /** The market it is built and was run for; picking it fills these in. */
  builtFor: { provider: "binance"; market: "futures" | "spot"; symbol: string; timeframe: string };
  /** What the results are measured on, e.g. "$100 a trade at 10x". */
  capital: string;
  author: string;
  /** What to keep in mind before going live with it. */
  caution?: string;
  results: YearResult[];
  /** When the results were last produced by our engine. */
  verifiedOn: string;
};

const VERIFIED = "2026-11-03";
// For scripts whose settings were searched on the very years shown.
const TUNED =
  "Tuned on 2023–2026, so these years are not a test of it: expect less from here on. Built for this one coin; start small.";
// 2026 runs to 1 October.
const yr = (year: string, pnl: number, won: number, trades: number, maxDrawdown: number): YearResult => ({
  year,
  pnl,
  won,
  trades,
  maxDrawdown,
});

export const STRATEGY_LIBRARY: LibraryStrategy[] = [
  {
    id: "trend-ema",
    name: "Trend A · EMA 10/30",
    category: "Trend",
    level: "Medium",
    summary:
      "Goes long or short when the 10 EMA crosses the 30 EMA on 4h candles, only the way BTC's 200 EMA points. It exits on the opposite cross; an ATR stop caps a bad entry, and winners run with no take-profit.",
    builtFor: { provider: "binance", market: "futures", symbol: "ETHUSDT", timeframe: "4h" },
    capital: "$100 a trade, no compounding",
    author: "TradeStorm",
    caution:
      "Textbook settings, not tuned. Trend following loses in choppy years and earns in trending ones: judge it over 6–12 months and on several coins.",
    results: [yr("2024", 38.94, 10, 37, 50.31), yr("2025", 33.78, 13, 43, 41.65), yr("2026", -28.29, 4, 29, 56.95)],
    verifiedOn: VERIFIED,
  },
  {
    id: "trend-donchian",
    name: "Trend B · Donchian 20",
    category: "Breakout",
    level: "Medium",
    summary:
      "Buys a break above the 20-candle high and sells a break below the 20-candle low on 2h candles, only the way BTC's 200 EMA points. It exits on the opposite breakout; an ATR stop caps a bad entry.",
    builtFor: { provider: "binance", market: "futures", symbol: "BTCUSDT", timeframe: "2h" },
    capital: "$100 a trade, no compounding",
    author: "TradeStorm",
    caution:
      "Textbook settings, not tuned. Breakouts fail often in sideways markets; most of the profit comes from a few long trends.",
    results: [yr("2024", 14.65, 24, 79, 27.39), yr("2025", -16.34, 9, 40, 24.17), yr("2026", -3.6, 13, 68, 28)],
    verifiedOn: VERIFIED,
  },
  {
    id: "turtle-breakout",
    name: "Turtle breakout",
    category: "Breakout",
    level: "Simple",
    summary:
      "The classic 1980s trend system on daily candles: goes long on a close above the 55-day high and short below the 55-day low, and exits on a close beyond the 20-day channel. An ATR stop caps a false breakout.",
    builtFor: { provider: "binance", market: "futures", symbol: "ETHUSDT", timeframe: "1d" },
    capital: "$100 a trade, no leverage, no compounding",
    author: "TradeStorm",
    caution:
      "Textbook settings, not tuned. Few trades a year, and most of the profit comes from one or two long trends. Of BTC, ETH and SOL it did best on ETH; it also made money on BTC.",
    results: [yr("2024", 11.28, 3, 7, 19.18), yr("2025", 48.77, 3, 7, 31.65), yr("2026", 63.06, 3, 4, 8.03)],
    verifiedOn: VERIFIED,
  },
  {
    id: "ichimoku-trend",
    name: "Ichimoku trend",
    category: "Trend",
    level: "Medium",
    summary:
      "Trades with the Ichimoku cloud on 4h candles: long when the conversion line crosses above the base line with price above the cloud, short the mirror. It exits when price closes back across the base line.",
    builtFor: { provider: "binance", market: "futures", symbol: "ETHUSDT", timeframe: "4h" },
    capital: "$100 a trade, no leverage, no compounding",
    author: "TradeStorm",
    caution:
      "Textbook settings (9/26/52), not tuned. Of BTC, ETH and SOL it did best on ETH; on BTC it lost a little. Wins are rare but larger than the losses.",
    results: [yr("2024", 62.37, 14, 34, 23.92), yr("2025", 27.56, 11, 39, 21.24), yr("2026", 8.13, 8, 33, 47.17)],
    verifiedOn: VERIFIED,
  },
  {
    id: "squeeze-breakout",
    name: "Squeeze breakout",
    category: "Breakout",
    level: "Medium",
    summary:
      "Waits for a quiet market, when the Bollinger bands sit inside the Keltner channel, then trades the move that ends it, the way price closed against the 20 SMA. It exits when price closes back across the 20 SMA.",
    builtFor: { provider: "binance", market: "futures", symbol: "ETHUSDT", timeframe: "4h" },
    capital: "$100 a trade, no leverage, no compounding",
    author: "TradeStorm",
    caution:
      "Textbook settings, not tuned. Of BTC, ETH and SOL it did best on ETH; on SOL it lost a little. Many breakouts fail, so expect long losing streaks.",
    results: [yr("2024", 37.05, 23, 69, 37.69), yr("2025", 18.74, 20, 59, 33.35), yr("2026", -12.18, 11, 44, 52.11)],
    verifiedOn: VERIFIED,
  },
  {
    id: "macd-trend",
    name: "MACD with a trend filter",
    category: "Trend",
    level: "Simple",
    summary:
      "The MACD cross, taken only the way the 200 EMA points: long above it, short below it, out on the opposite cross. Compare it with the plain MACD script to see what one filter changes.",
    builtFor: { provider: "binance", market: "futures", symbol: "ETHUSDT", timeframe: "4h" },
    capital: "$100 a trade, no leverage, no compounding",
    author: "TradeStorm",
    caution:
      "Textbook settings (12/26/9), not tuned. Close to flat outside 2025. Of BTC, ETH and SOL it did best on ETH.",
    results: [yr("2024", -4.02, 17, 73, 35.18), yr("2025", 86.49, 27, 67, 19.07), yr("2026", -13.42, 16, 58, 54.21)],
    verifiedOn: VERIFIED,
  },
  {
    id: "dca-weekly",
    name: "Weekly DCA",
    category: "Accumulation",
    level: "Simple",
    summary:
      "Buys $25 of SOL every week whatever the price, and sells the whole stack once it is 15% above its average price, then starts again. No leverage and no charts to read.",
    builtFor: { provider: "binance", market: "spot", symbol: "SOLUSDT", timeframe: "1d" },
    capital: "$25 a week, up to $2,000 in all",
    author: "TradeStorm",
    caution:
      "It has no stop-loss: in a long fall it keeps buying and waits, so money can stay tied up for months. It needs a coin that recovers. It also made money on ETH; on BTC our engine placed no orders at $25.",
    results: [yr("2024", 196.35, 46, 54, 51.39), yr("2025", 73.69, 33, 53, 119.26), yr("2026", 158.38, 33, 40, 194.14)],
    verifiedOn: VERIFIED,
  },
  {
    id: "cross-sol-highwr",
    name: "Cross SOL 4h HighWR",
    category: "Trend",
    level: "Advanced",
    summary:
      "A moving-average cross on SOL 4h candles with volume, RSI and trend filters, a stop-loss and a take-profit on every trade. Many small wins; a loss costs more than a win pays.",
    builtFor: { provider: "binance", market: "futures", symbol: "SOLUSDT", timeframe: "4h" },
    capital: "$100 margin a trade at 10x, no compounding",
    author: "TradeStorm",
    caution:
      "Tuned on 2023–2026, so these years are not a test. Similar settings picked on 2023–25 made money in 2026 only 38% of the time. Start small.",
    results: [yr("2024", 309.24, 17, 20, 44.4), yr("2025", 257.31, 10, 10, 24.66), yr("2026", 189.5, 10, 11, 43.73)],
    verifiedOn: VERIFIED,
  },
  {
    id: "cross-wld-highwr",
    name: "Cross WLD 4h HighWR",
    category: "Trend",
    level: "Advanced",
    summary:
      "A moving-average cross on WLD 4h candles with a stop-loss and a take-profit on every trade. Many small wins; a loss costs a bit more than a win pays.",
    builtFor: { provider: "binance", market: "futures", symbol: "WLDUSDT", timeframe: "4h" },
    capital: "$100 margin a trade at 10x, no compounding",
    author: "TradeStorm",
    caution:
      "Tuned on 2023–2026, so these years are not a test. Similar settings picked on 2023–25 made money in 2026 only 38% of the time. Start small.",
    results: [yr("2024", 327.7, 15, 20, 82.39), yr("2025", 358.51, 14, 17, 103.75), yr("2026", 344.4, 14, 17, 47.66)],
    verifiedOn: VERIFIED,
  },
  {
    id: "cross-arb-highwr",
    name: "Cross ARB 4h HighWR",
    category: "Trend",
    level: "Advanced",
    summary:
      "A moving-average cross on ARB 4h candles (WMA 22/64 cross with ADX, RSI and trend filters), with a stop-loss and a take-profit on every trade. Many small wins; a loss costs more than a win pays.",
    builtFor: { provider: "binance", market: "futures", symbol: "ARBUSDT", timeframe: "4h" },
    capital: "$100 margin a trade at 10x, no compounding",
    author: "TradeStorm",
    caution: TUNED,
    results: [yr("2024", 350.19, 14, 14, 27.81), yr("2025", 302.95, 16, 19, 57.85), yr("2026", 278.89, 12, 13, 30.72)],
    verifiedOn: VERIFIED,
  },
  {
    id: "cross-jup-highwr",
    name: "Cross JUP 4h HighWR",
    category: "Trend",
    level: "Advanced",
    summary:
      "A moving-average cross on JUP 4h candles (WMA 13/48 cross with ADX, RSI and trend filters; no trades at weekends), with a stop-loss and a take-profit on every trade. Many small wins; a loss costs more than a win pays.",
    builtFor: { provider: "binance", market: "futures", symbol: "JUPUSDT", timeframe: "4h" },
    capital: "$100 margin a trade at 10x, no compounding",
    author: "TradeStorm",
    caution: TUNED,
    results: [yr("2024", 381.52, 14, 15, 53.88), yr("2025", 318.28, 15, 18, 103.37), yr("2026", 318.19, 12, 13, 47.52)],
    verifiedOn: VERIFIED,
  },
  {
    id: "cross-ondo-highwr",
    name: "Cross ONDO 4h HighWR",
    category: "Trend",
    level: "Advanced",
    summary:
      "A moving-average cross on ONDO 4h candles (EMA 20/50 cross with ADX and slope filters), with a stop-loss and a take-profit on every trade. Many small wins; a loss costs more than a win pays.",
    builtFor: { provider: "binance", market: "futures", symbol: "ONDOUSDT", timeframe: "4h" },
    capital: "$100 margin a trade at 10x, no compounding",
    author: "TradeStorm",
    caution: TUNED,
    results: [yr("2024", 426.12, 17, 20, 51.59), yr("2025", 284.09, 15, 19, 98.08), yr("2026", 176.48, 12, 17, 84.86)],
    verifiedOn: VERIFIED,
  },
  {
    id: "cross-tao-highwr",
    name: "Cross TAO 4h HighWR",
    category: "Trend",
    level: "Advanced",
    summary:
      "A moving-average cross on TAO 4h candles (EMA 4/192 cross with ADX, volume and trend filters), with a stop-loss and a take-profit on every trade. Many small wins; a loss costs more than a win pays.",
    builtFor: { provider: "binance", market: "futures", symbol: "TAOUSDT", timeframe: "4h" },
    capital: "$100 margin a trade at 10x, no compounding",
    author: "TradeStorm",
    caution: TUNED,
    results: [yr("2024", 290.24, 13, 14, 44.37), yr("2025", 295.88, 15, 17, 47.81), yr("2026", 160.98, 8, 9, 63.78)],
    verifiedOn: VERIFIED,
  },
  {
    id: "cross-op-highwr",
    name: "Cross OP 4h HighWR",
    category: "Trend",
    level: "Advanced",
    summary:
      "A moving-average cross on OP 4h candles (SMA 4/60 cross with an ADX filter), with a stop-loss and a take-profit on every trade. Many small wins; a loss costs more than a win pays.",
    builtFor: { provider: "binance", market: "futures", symbol: "OPUSDT", timeframe: "4h" },
    capital: "$100 margin a trade at 10x, no compounding",
    author: "TradeStorm",
    caution: TUNED,
    results: [yr("2024", 238.18, 15, 19, 91.52), yr("2025", 293.67, 14, 16, 49.58), yr("2026", 233.76, 11, 12, 41.48)],
    verifiedOn: VERIFIED,
  },
  {
    id: "cross-inj-highwr",
    name: "Cross INJ 4h HighWR",
    category: "Trend",
    level: "Advanced",
    summary:
      "A moving-average cross on INJ 4h candles (SMA 12/80 cross with ADX and slope filters), with a stop-loss and a take-profit on every trade. Many small wins; a loss costs more than a win pays.",
    builtFor: { provider: "binance", market: "futures", symbol: "INJUSDT", timeframe: "4h" },
    capital: "$100 margin a trade at 10x, no compounding",
    author: "TradeStorm",
    caution: TUNED,
    results: [yr("2024", 311.61, 13, 14, 53.64), yr("2025", 188.37, 16, 22, 80.23), yr("2026", 94.54, 5, 6, 31.58)],
    verifiedOn: VERIFIED,
  },
  {
    id: "cross-bch-highwr",
    name: "Cross BCH 4h HighWR",
    category: "Trend",
    level: "Advanced",
    summary:
      "A moving-average cross on BCH 4h candles (SMA 13/80 cross with RSI and trend filters), with a stop-loss and a take-profit on every trade. Many small wins; a loss costs more than a win pays.",
    builtFor: { provider: "binance", market: "futures", symbol: "BCHUSDT", timeframe: "4h" },
    capital: "$100 margin a trade at 10x, no compounding",
    author: "TradeStorm",
    caution: TUNED,
    results: [yr("2024", 115.77, 15, 18, 62.45), yr("2025", 138.48, 19, 23, 105.78), yr("2026", 156.19, 12, 13, 72.45)],
    verifiedOn: VERIFIED,
  },
  {
    id: "cross-uni",
    name: "Cross UNI 4h",
    category: "Trend",
    level: "Advanced",
    summary:
      "A moving-average cross on UNI 4h candles (VWMA 13/60 with volume, RSI and trend filters). The opposite of HighWR: it wins less than half the time, but a win pays about 2.5 times what a loss costs.",
    builtFor: { provider: "binance", market: "futures", symbol: "UNIUSDT", timeframe: "4h" },
    capital: "$100 margin a trade at 10x, no compounding",
    author: "TradeStorm",
    caution: TUNED,
    results: [yr("2024", 386.37, 9, 18, 114.88), yr("2025", 629.94, 11, 17, 63.51), yr("2026", 170.84, 4, 8, 140.49)],
    verifiedOn: VERIFIED,
  },
  {
    id: "supertrend",
    name: "Supertrend",
    category: "Learning",
    level: "Simple",
    summary: "Follows the trend: flips side when price crosses the ATR band. A short script to see how a strategy is built.",
    builtFor: { provider: "binance", market: "futures", symbol: "BTCUSDT", timeframe: "4h" },
    capital: "$10,000, all of it in every trade",
    author: "TradeStorm",
    caution: "A teaching example with default settings, not a strategy to trade as it is.",
    results: [yr("2024", 3754.21, 15, 69, 2705.86), yr("2025", -3545.88, 12, 58, 4158.26), yr("2026", -930.72, 11, 64, 2103.16)],
    verifiedOn: VERIFIED,
  },
  {
    id: "macd",
    name: "MACD",
    category: "Learning",
    level: "Simple",
    summary: "Goes long when the MACD line crosses above its signal line and short on the cross down. A short script to see how a strategy is built.",
    builtFor: { provider: "binance", market: "futures", symbol: "BTCUSDT", timeframe: "4h" },
    capital: "$10,000, all of it in every trade",
    author: "TradeStorm",
    caution: "A teaching example with default settings, not a strategy to trade as it is.",
    results: [yr("2024", -4868.13, 41, 217, 5874.3), yr("2025", -5803.86, 32, 187, 5794.88), yr("2026", -854.76, 28, 128, 3310.53)],
    verifiedOn: VERIFIED,
  },
  {
    id: "rsi",
    name: "RSI",
    category: "Learning",
    level: "Simple",
    summary: "Goes long when RSI climbs out of oversold and short when it drops out of overbought. A short script to see how a strategy is built.",
    builtFor: { provider: "binance", market: "futures", symbol: "BTCUSDT", timeframe: "4h" },
    capital: "$10,000, all of it in every trade",
    author: "TradeStorm",
    caution: "A teaching example with default settings, not a strategy to trade as it is.",
    results: [yr("2024", -5453.77, 7, 53, 5784.87), yr("2025", -408.62, 12, 40, 3015.33), yr("2026", -2862, 6, 35, 4610.12)],
    verifiedOn: VERIFIED,
  },
];

const sources = import.meta.glob<string>("./*.pine", { query: "?raw", import: "default" });

/** The Pine Script of a library strategy, loaded on demand. */
export async function loadLibrarySource(id: string): Promise<string> {
  const load = sources[`./${id}.pine`];
  if (!load) throw new Error(`No script for "${id}" in the library`);
  return load();
}

/** All years together: net profit, and trades won out of trades closed. */
export function totalResult(s: LibraryStrategy): { pnl: number; won: number; trades: number } {
  return s.results.reduce((t, r) => ({ pnl: t.pnl + r.pnl, won: t.won + r.won, trades: t.trades + r.trades }), {
    pnl: 0,
    won: 0,
    trades: 0,
  });
}
